import { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

const AlertContext = createContext(null);

const ICONS = {
    success: { Icon: CheckCircle2, color: '#22c55e', bg: '#f0fdf4' },
    warning: { Icon: AlertTriangle, color: '#f59e0b', bg: '#fffbeb' },
    error: { Icon: XCircle, color: '#ef4444', bg: '#fef2f2' },
    info: { Icon: Info, color: '#3b82f6', bg: '#eff6ff' },
};

export const AlertProvider = ({ children }) => {
    const [alerts, setAlerts] = useState([]);
    const [confirmState, setConfirmState] = useState(null);

    const showAlert = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now() + Math.random();
        setAlerts(prev => [...prev, { id, message, type }]);
        if (duration > 0) {
            setTimeout(() => {
                setAlerts(prev => prev.filter(a => a.id !== id));
            }, duration);
        }
    }, []);

    const showConfirm = useCallback((message, { title, confirmText = 'Onayla', cancelText = 'Vazgeç', type = 'warning' } = {}) => {
        return new Promise((resolve) => {
            setConfirmState({ message, title, confirmText, cancelText, type, resolve });
        });
    }, []);

    const handleConfirm = (result) => {
        confirmState?.resolve(result);
        setConfirmState(null);
    };

    const dismissAlert = (id) => {
        setAlerts(prev => prev.filter(a => a.id !== id));
    };

    return (
        <AlertContext.Provider value={{ showAlert, showConfirm }}>
            {children}

            {/* Toast Alerts */}
            <div style={{
                position: 'fixed',
                top: '1rem',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '90%',
                maxWidth: '400px',
                pointerEvents: 'none',
            }}>
                {alerts.map((alert) => {
                    const { Icon, color, bg } = ICONS[alert.type] || ICONS.info;
                    return (
                        <div
                            key={alert.id}
                            style={{
                                pointerEvents: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '14px 16px',
                                background: bg,
                                border: `1px solid ${color}25`,
                                borderRadius: '16px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                animation: 'alertSlideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                            }}
                        >
                            <Icon size={20} style={{ color, flexShrink: 0 }} />
                            <span style={{ flex: 1, fontSize: '0.875rem', fontWeight: 600, color: '#1f2937', lineHeight: 1.4 }}>
                                {alert.message}
                            </span>
                            <button
                                onClick={() => dismissAlert(alert.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', flexShrink: 0 }}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Confirm Modal */}
            {confirmState && (
                <>
                    <div
                        onClick={() => handleConfirm(false)}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.4)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 10001,
                            animation: 'alertFadeIn 0.2s ease',
                        }}
                    />
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10002,
                        width: '90%',
                        maxWidth: '380px',
                        background: 'white',
                        borderRadius: '20px',
                        padding: '1.5rem',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        animation: 'alertModalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}>
                        {(() => {
                            const { Icon, color } = ICONS[confirmState.type] || ICONS.warning;
                            return (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    <div style={{
                                        width: '56px',
                                        height: '56px',
                                        borderRadius: '16px',
                                        background: `${color}12`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '1rem',
                                    }}>
                                        <Icon size={28} style={{ color }} />
                                    </div>
                                    {confirmState.title && (
                                        <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 0.5rem 0', color: '#1f2937' }}>
                                            {confirmState.title}
                                        </h3>
                                    )}
                                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: '0 0 1.5rem 0', lineHeight: 1.5, fontWeight: 500 }}>
                                        {confirmState.message}
                                    </p>
                                    <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                        <button
                                            onClick={() => handleConfirm(false)}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                border: '1px solid #e5e7eb',
                                                background: 'white',
                                                fontSize: '0.875rem',
                                                fontWeight: 700,
                                                color: '#6b7280',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {confirmState.cancelText}
                                        </button>
                                        <button
                                            onClick={() => handleConfirm(true)}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                borderRadius: '12px',
                                                border: 'none',
                                                background: color,
                                                fontSize: '0.875rem',
                                                fontWeight: 700,
                                                color: 'white',
                                                cursor: 'pointer',
                                                boxShadow: `0 4px 12px ${color}40`,
                                            }}
                                        >
                                            {confirmState.confirmText}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </>
            )}

            <style>{`
                @keyframes alertSlideIn {
                    from { transform: translateY(-20px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes alertFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes alertModalIn {
                    from { transform: translate(-50%, -45%); opacity: 0; }
                    to { transform: translate(-50%, -50%); opacity: 1; }
                }
            `}</style>
        </AlertContext.Provider>
    );
};

export const useAlert = () => {
    const ctx = useContext(AlertContext);
    if (!ctx) throw new Error('useAlert must be used within AlertProvider');
    return ctx;
};
